import { useEffect } from 'react';
import { useSystemStore } from '@/store/systemStore';

/**
 * Hook for dynamic browser context (title and favicon).
 * Prefixes the title with [DEV] or [TEST] based on environment settings.
 * 
 * @param title Optional title to set. Defaults to 'BuildPoint'.
 */
let activePageTitle: string | null = null;

function applyTitle(env: string | undefined) {
  const baseTitle = 'BuildPoint';
  const prefix =
    env && env !== 'production' ? (env === 'development' ? '[DEV] ' : '[TEST] ') : '';
  document.title = `${prefix}${activePageTitle ? `${activePageTitle} | ${baseTitle}` : baseTitle}`;
}

export const useBrowserContext = (title?: string) => {
  const { settings } = useSystemStore();
  const env = settings?.environment_type;

  useEffect(() => {
    // T2737: ob odhodu s strani vrni osnovni naslov. Korenski klic v `App`
    // (brez naslova) teče samo ob spremembi okolja, zato je naslov prejšnje
    // strani sicer obvisel — po prijavi je `/dashboard` kazal »[DEV] Prijava |
    // BuildPoint«. Naslov aktivne strani hrani modul, da korenski klic (ki
    // teče za učinki otrok) naslova strani ne povozi z osnovnim.
    if (title) activePageTitle = title;
    applyTitle(env);
    if (!title) return undefined;
    return () => {
      if (activePageTitle === title) activePageTitle = null;
      applyTitle(env);
    };
  }, [title, env]);

  useEffect(() => {
    // Only update favicon in non-production environments
    if (!env || env === 'production') return;

    const updateFavicon = () => {
      const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
      if (!link) return;

      const canvas = document.createElement('canvas');
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Draw original favicon (optional, but we'll just draw a colored circle/badge)
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, 32, 32);
        
        // Add environment badge
        ctx.beginPath();
        ctx.arc(24, 8, 7, 0, 2 * Math.PI);
        ctx.fillStyle = env === 'development' ? '#3b82f6' : '#a855f7'; // Blue for dev, Purple for test
        ctx.fill();
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 2;
        ctx.stroke();

        link.href = canvas.toDataURL('image/png');
      };
      img.src = '/favicon.svg';
    };

    updateFavicon();
  }, [env]);
};
